import { makeAutoObservable, runInAction } from 'mobx'
import moment from 'moment-timezone'
import AdminApi from './../../../api/admin-api/admin-api.js'
import EnrolledLeaderboardService from './../../../shared/services/EnrolledLeaderboard.service'
import ActivityStore from './../../../shared/stores/activity.store'

// Periods roll over on US Eastern time, whatever the TV's clock says.
const TIMEZONE = 'America/New_York'
const RETRY_SECONDS = 15

export const BOARDS = [
  { value: 'leaderboard', label: 'Leaderboard' },
  { value: 'points', label: 'Points' },
  { value: 'av', label: 'AV' },
  { value: 'enrolled', label: 'Enrolled Agents' },
]

export const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'Year to Date' },
]

export const METRICS = [
  { value: 'points', label: 'Points' },
  { value: 'av', label: 'AV' },
  { value: 'aca_lives', label: 'ACA Lives' },
  { value: 'med_adv', label: 'Med Advantage' },
]

export const GROUPINGS = [
  { value: 'user_id', label: 'Sig Agents' },
  { value: 'elite_agency_id', label: 'Elite Teams' },
  { value: 'sub_agent_id', label: 'Career Agents' },
  { value: 'district', label: 'District Teams' },
  { value: 'region', label: 'Region Teams' },
  { value: 'division', label: 'Division Teams' },
]

export const CATEGORIES = ['HEALTH', 'LIFE', 'SENIOR', 'GROUP']

export const ROTATIONS = [
  { value: 0, label: 'Off' },
  { value: 15, label: '15 s' },
  { value: 30, label: '30 s' },
  { value: 60, label: '60 s' },
  { value: 120, label: '2 m' },
]

export const REFRESHES = [
  { value: 30, label: '30 s' },
  { value: 60, label: '60 s' },
  { value: 300, label: '5 m' },
]

export const DEFAULT_SETTINGS = {
  boards: ['leaderboard'],
  period: 'week',
  metric: 'points',
  group: 'user_id',
  category: '',
  no_aca: false,
  division_id: '',
  team_id: '',
  rotate: 30,
  refresh: 60,
}

// Leader Boards output_mode -> Big Screen period.
export const OUTPUT_MODE_PERIODS = { weekly: 'week', monthly: 'month', quarterly: 'quarter', yearly: 'ytd' }

const oneOf = (value, options, fallback) => (options.some(o => `${o.value}` === `${value}`) ? value : fallback)

export const parseSettings = search => {
  const q = new URLSearchParams(search || '')
  const d = DEFAULT_SETTINGS

  const boards = [...new Set(`${q.get('boards') || ''}`.split(',').filter(b => BOARDS.some(o => o.value === b)))]

  return {
    boards: boards.length ? boards : d.boards,
    period: oneOf(q.get('period'), PERIODS, d.period),
    metric: oneOf(q.get('metric'), METRICS, d.metric),
    group: oneOf(q.get('group'), GROUPINGS, d.group),
    category: CATEGORIES.includes(q.get('category')) ? q.get('category') : d.category,
    no_aca: q.get('no_aca') === '1',
    division_id: /^\d+$/.test(q.get('division_id') || '') ? q.get('division_id') : d.division_id,
    team_id: /^\d+$/.test(q.get('team_id') || '') ? q.get('team_id') : d.team_id,
    rotate: parseInt(oneOf(q.get('rotate'), ROTATIONS, d.rotate), 10),
    refresh: parseInt(oneOf(q.get('refresh'), REFRESHES, d.refresh), 10),
  }
}

export const toSearch = settings => {
  const s = { ...DEFAULT_SETTINGS, ...settings }
  const q = new URLSearchParams()

  q.set('boards', s.boards.join(','))
  q.set('period', s.period)
  q.set('metric', s.metric)
  q.set('group', s.group)
  if (s.category) q.set('category', s.category)
  if (s.no_aca) q.set('no_aca', '1')
  if (s.division_id && s.group === 'user_id') q.set('division_id', s.division_id)
  if (s.team_id && s.group === 'sub_agent_id') q.set('team_id', s.team_id)
  q.set('rotate', `${s.rotate}`)
  q.set('refresh', `${s.refresh}`)

  return `?${q.toString().replace(/%2C/g, ',')}`
}

export const getPeriodRange = period => {
  const now = moment.tz(TIMEZONE),
    fmt = m => m.format('YYYY-MM-DD')

  switch (period) {
    case 'today':
      return { start: fmt(now), stop: fmt(now) }
    case 'month':
      return { start: fmt(now.clone().startOf('month')), stop: fmt(now.clone().endOf('month')) }
    case 'quarter':
      return { start: fmt(now.clone().startOf('quarter')), stop: fmt(now.clone().endOf('quarter')) }
    case 'ytd':
      return { start: fmt(now.clone().startOf('year')), stop: fmt(now) }
    case 'week':
    default:
      return { start: fmt(now.clone().startOf('isoWeek')), stop: fmt(now.clone().endOf('isoWeek')) }
  }
}

const fetchLeaderboardRows = async (type, start, stop, categories, agentType, opts) => {
  // 'totals' is no timespan breakdown: Big Screen shows each row's total only.
  const res = await AdminApi.getLeaderboardData(type, start, stop, categories, agentType, 'totals', opts)
  const rows = res?.data?.payload?.rows
  if (!Array.isArray(rows)) throw new Error('Leaderboard data unavailable.')
  return rows
}

const fetchEnrolledRows = async (start, stop) => {
  const res = await EnrolledLeaderboardService.get(start, stop)
  if (!Array.isArray(res?.rows)) throw new Error(res?.error || 'Enrolled agents unavailable.')
  return res.rows
}

const fetchBoardRows = (board, settings, { start, stop }) => {
  switch (board) {
    case 'points':
    case 'av':
      return fetchLeaderboardRows(board, start, stop, null, 'user_id', { scope: 'downline' })
    case 'enrolled':
      return fetchEnrolledRows(start, stop)
    case 'leaderboard':
    default:
      return fetchLeaderboardRows(
        settings.metric,
        start,
        stop,
        settings.category ? [settings.category] : null,
        settings.group,
        {
          remove_aca_av: settings.metric === 'av' && settings.no_aca,
          division_id: settings.group === 'user_id' ? settings.division_id || undefined : undefined,
          team_id: settings.group === 'sub_agent_id' ? settings.team_id || undefined : undefined,
        }
      )
  }
}

class BigScreenStore {
  constructor() {
    makeAutoObservable(this)
  }

  settings = { ...DEFAULT_SETTINGS }
  rowsByBoard = {}
  range = getPeriodRange(DEFAULT_SETTINGS.period)
  lastUpdatedAt = null
  refreshFailed = false
  isLoading = false

  _running = false
  _timer = null
  _inFlight = false
  _queued = false
  _version = 0

  get hasData() {
    return Object.keys(this.rowsByBoard).length > 0
  }

  applySettings = settings => {
    const before = JSON.stringify(this.settings)
    this.settings = settings
    if (this._running && before !== JSON.stringify(settings)) {
      // Rows from the old settings no longer describe the boards on screen.
      this._version++
      this.rowsByBoard = {}
      this.lastUpdatedAt = null
      this.refreshFailed = false
      this.isLoading = true
      this.refresh()
    }
  }

  start = () => {
    this._running = true
    this.refresh()
  }

  stop = () => {
    this._running = false
    this._version++
    clearTimeout(this._timer)
    this._timer = null
    this._inFlight = false
    this._queued = false
    this.rowsByBoard = {}
    this.lastUpdatedAt = null
    this.refreshFailed = false
  }

  // Re-fetches when the period rolled over (e.g. Monday 00:00 Eastern for "This Week").
  checkPeriodRollover = () => {
    const range = getPeriodRange(this.settings.period)
    if (range.start !== this.range.start || range.stop !== this.range.stop) this.refresh()
  }

  refresh = async () => {
    if (!this._running) return
    if (this._inFlight) {
      this._queued = true
      return
    }

    clearTimeout(this._timer)
    this._inFlight = true
    this.isLoading = !this.hasData

    const version = this._version,
      settings = this.settings,
      range = getPeriodRange(settings.period)

    // Keeps the 1-hour inactivity logout from ending an unattended screen,
    // including one that only shows the Enrolled board (not an admin-api call).
    ActivityStore.recordRequest()

    let rowsByBoard = null
    try {
      const results = await Promise.all(settings.boards.map(board => fetchBoardRows(board, settings, range)))
      rowsByBoard = Object.fromEntries(settings.boards.map((board, i) => [board, results[i]]))
    } catch (ex) {
      console.error('[BigScreen] refresh failed', ex)
    }

    runInAction(() => {
      this._inFlight = false
      if (version !== this._version || !this._running) return

      this.isLoading = false

      if (rowsByBoard) {
        this.rowsByBoard = rowsByBoard
        this.range = range
        this.lastUpdatedAt = new Date()
        this.refreshFailed = false
      } else {
        this.refreshFailed = true
      }
    })

    if (!this._running) return
    if (this._queued) {
      this._queued = false
      return this.refresh()
    }

    const delay = this.refreshFailed ? Math.min(RETRY_SECONDS, this.settings.refresh) : this.settings.refresh
    this._timer = setTimeout(this.refresh, delay * 1000)
  }
}

export default new BigScreenStore()
